/**
 * Поле поиска — одно на всё приложение.
 *
 * Раньше каждая страница собирала его сама: `position: relative` вокруг Input,
 * иконка абсолютом и подобранный на глаз `left`. Отсюда и разъезжались:
 * где-то иконка центрировалась по строке вместе с её паддингом (и стояла
 * выше центра поля), где-то отступ под неё не совпадал с паддингом текста, а
 * подпись жила обычным placeholder'ом и исчезала при вводе — в отличие от
 * остальных полей приложения с плавающим лейблом.
 *
 * Подписи над полем у поиска нет, хотя у полей формы она есть с 29.09.2026:
 * строка поиска стоит в полосе контролов рядом с кнопками, и подпись сверху
 * делала бы её выше соседей. `label` поэтому становится плейсхолдером (если
 * свой не задан) и доступным именем. Иконка и крестик центрируются по самому
 * полю и лежат в его боковых вылетах — текст под них не заезжает.
 *
 * @use строка поиска над списком.
 */

import * as React from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { Input } from '@/shared/ui/Input/Input';
import styles from './SearchInput.module.css';

export interface SearchInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value' | 'type'> {
  value: string;
  onChange: (value: string) => void;
  label: string;
  className?: string;
}

export const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  function SearchInput({ value, onChange, label, className, placeholder, ...rest }, ref) {
    return (
      <div className={cn(styles.wrap, className)} data-clearable={value !== '' || undefined}>
        <Search size={16} className={styles.icon} aria-hidden />
        <Input
          ref={ref}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder ?? label}
          aria-label={label}
          className={styles.input}
          {...rest}
        />
        {value !== '' && (
          <button
            type="button"
            className={styles.clear}
            onClick={() => onChange('')}
            aria-label="Очистить поиск"
          >
            <X size={14} aria-hidden />
          </button>
        )}
      </div>
    );
  },
);
